import React, { VFC, useState } from 'react';
import { isEmpty } from '@dxb-web-ui-framework/common/utils';
import { useTranslation } from 'react-i18next';
import { DataServiceHookResponse } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/types';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import { useNavigate } from 'react-router-dom';
import {
  useWizardGoToNextStep,
  useWizardStepData,
} from '../../../../content-composer/src/contexts/ClientSideWizard/hooks';
import { ECOMMBXLoader } from '../../../../content-composer/src/components/Generic/ECOMMBXLoader/ECOMMBXLoader';
import { ErrorContent } from '../../../../content-composer/src/components/Partials/ErrorContent';
import { InAPP2FAStep } from '../../../../content-composer/src/components/ClientSideWizard/Steps';
import { AuthenticationMethodEnum } from '../../../../content-composer/src/components/ClientSideWizard/Steps/InApp2FAStep/InAPP2FAStep';
import { getRoutePath } from '../../../../content-composer/src/components/Routes/routePaths';
import { useSelectedCustomerId } from '../../../../userProfile/src/contexts/UserInfo/hooks';
import { useAuthorizationLoadingState, useAuthorizationErrorState, useRequestNewRateFlag } from '../../contexts';
import { InitiateMandateResponseBody } from '../../domain/Authorization/entities/InitiateMandateResponseDTO';
import { useMandateService } from './useMandateService';

export interface MandateInitiateStepProps {
  authAction: string | null;
  hasRejectionReason?: boolean;
  isBulk?: boolean;
  onToggleDrawer?: () => void;
  resetFlow?: () => void;
}

const MandateInitiateStep: VFC<MandateInitiateStepProps> = ({
  authAction,
  hasRejectionReason = false,
  isBulk = false,
  onToggleDrawer,
  resetFlow,
}) => {
  const { t } = useTranslation();
  const [shouldInitiate, setShouldInitiate] = useState(true);
  const goToNextWizardStep = useWizardGoToNextStep();
  const requestSpecialRate = useRequestNewRateFlag();
  const selectedCustomerId = useSelectedCustomerId();
  const navigate = useNavigate();
  const previousStepData = useWizardStepData(hasRejectionReason ? 'rejectReasonStep' : undefined) as {
    values: { cancellationReason?: string; requestSpecialRate?: boolean };
  };

  // loading state
  const isLoadingCheckAuthorization = useAuthorizationLoadingState('isLoadingCheckAuthorization');

  // error state
  const transactionDetailsError = useAuthorizationErrorState('transactionDetailsError');
  // service
  const {
    data: initiateMandateData,
    isLoading: isLoadingInitiateAuthorization,
    error: initiateAuthorizationError,
    mutate: refetchInitiateMandateData,
  } = (
    useMandateService(isBulk, 'initiate') as (
      authAction: Nullable<string>,
      cancellationReason?: string,
      dealerRateRequest?: boolean,
      shouldInitiate?: boolean,
    ) => DataServiceHookResponse<InitiateMandateResponseBody>
  )(authAction, previousStepData.values?.cancellationReason, requestSpecialRate, shouldInitiate);

  const isLoading = isLoadingCheckAuthorization || isLoadingInitiateAuthorization;
  const error = transactionDetailsError || initiateAuthorizationError;

  const onResetFlow = () => {
    setShouldInitiate(false);
    resetFlow && resetFlow();
  };

  return (
    <>
      {error && !isLoading && (
        <ErrorContent
          code={t('authorizationsListPage.ApproveRejectFlow.ErrorContent.Title')}
          isNumericCode={false}
          onClickBtn={() => navigate(getRoutePath({ routeKey: 'dashboard', customerId: selectedCustomerId }))}
          subtitle={error?.message || t('authorizationsListPage.ApproveRejectFlow.ErrorContent.Execute.Subtitle')}
          btnLabel={t('authorizationsListPage.ApproveRejectFlow.ErrorContent.BtnLabel')}
        />
      )}
      {isLoading && <ECOMMBXLoader size='small' />}
      {!error && isEmpty(error) && !isLoading && (
        <InAPP2FAStep
          title={t(
            authAction === 'approve'
              ? 'authorizationsListPage.ApproveRejectFlow.MandateInitiateStep.inApp2FA.Approve.Title'
              : 'authorizationsListPage.ApproveRejectFlow.MandateInitiateStep.inApp2FA.Reject.Title',
          )}
          hasOverflowYAuto={true}
          resetFlow={onResetFlow}
          onToggleDrawer={onToggleDrawer}
          authenticationMethod={initiateMandateData?.authenticationMethod as AuthenticationMethodEnum}
          externalAuthorizationId={initiateMandateData?.authorizationId}
          externalOnGoToNextWizardStep={(data: any) =>
            goToNextWizardStep({ ...data, requestId: initiateMandateData?.requestId })
          }
          externalOnCancel={resetFlow}
        />
      )}
    </>
  );
};

export { MandateInitiateStep };
